import { FilterRow, ActiveFilters } from "@/ui/FilterRow";
import { useState } from "react";
import {
  accessListGlobal,
  accessApproveRequest,
  accessDecline,
  adminPeopleList,
  adminPersonOpen,
  adminSetCapabilities,
  adminViewAsStart,
  delegationListGlobal,
  delegationRevoke,
  type PersonRow,
} from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { Banner, Dialog, Empty, Field, KV, Pager, Pill, ReasonDialog, Receipt, RecordFields, Skeleton, Table, fmtDate, roleLabel, personLabel } from "@/ui";

export async function allPeople(): Promise<PersonRow[]> {
  const people: PersonRow[] = [];
  let cursor: string | null = null;
  do {
    const page = await adminPeopleList({ include_departed: true, limit: 100, cursor });
    people.push(...page.items);
    cursor = page.next_cursor ?? null;
  } while (cursor);
  return people;
}
/**
 * The Capability filter's options: the role vocabulary the roster shows (F-44), the eight roles in the permission
 * matrix's column order (models/core.py MATRIX_ROLES: the BRD's order with the dropped columns removed and the Admin
 * last, cockpit ruling 12 Oct 06:10; DC-228 as amended 12 Oct 05:29 folds TA Admin and HR Systems Admin into Admin and
 * HR Operations into Coordinator, and keeps the TA Manager), each named by roleLabel as the roster's pills are.
 */
export const CAPABILITIES = ["hm", "recruiter", "coordinator", "hrbp", "compensation", "interviewer", "ta_manager", "admin"] as const;
/** The capabilities the Set capabilities dialog grants: every one of the eight roles (ruling 12 Oct: the Admin assigns
 *  roles, so the dialog offers each role the matrix names, in its order; the server decides each grant again). */
const ROLES: readonly string[] = CAPABILITIES;
/** The global access requests and grants are the Root Owner's read (`access.list_global`): the section is drawn only where
 * the manifest carries it. A reader of People and capabilities without it keeps the roster on this tab, with no refusal
 * callout under it (demo smoke D11). */
export function showsGlobalAccess(can: (tool: string) => boolean): boolean {
  return can("access.list_global");
}

export function AdminPeople() {
  const { can } = useSession();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [cap, setCap] = useState("");
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const [person, setPerson] = useState<string | null>(null);
  const [lens, setLens] = useState(false);
  const [target, setTarget] = useState("");
  const q = useRead(["people", query, status, cap, pages.at(-1)], () =>
    adminPeopleList({ query: query || null, status: status || null, capability: cap || null, include_departed: true, limit: 25, cursor: pages.at(-1) ?? null }),
  );
  const everyone = useRead(["all-people"], allPeople, { enabled: lens });
  const start = useWrite(adminViewAsStart);
  const filter = (set: (v: string) => void, v: string) => {
    set(v);
    setPages([null]);
  };
  return (
    <section>
      <div className="hrow">
        <h2 className="sec sec--plain">People and capabilities</h2>
        <button className="btn btn--primary" onClick={() => setLens(true)}>
          View as
        </button>
      </div>
      <p className="muted">Standing capabilities come from identity. Employment status is read-only from the HRIS. Seats belong to each requisition.</p>
      <FilterRow>
        <Field label="Search people" id="people-query">
          <input id="people-query" value={query} onChange={(e) => filter(setQuery, e.target.value)} />
        </Field>
        <Field label="Employment status" id="people-status">
          <select id="people-status" value={status} onChange={(e) => filter(setStatus, e.target.value)}>
            <option value="">All, including departed</option>
            {["active", "pending", "inactive"].map((s) => (
              <option key={s} value={s}>{roleLabel(s)}</option>
            ))}
          </select>
        </Field>
        <Field label="Capability" id="people-cap">
          <select id="people-cap" value={cap} onChange={(e) => filter(setCap, e.target.value)}>
            <option value="">Any</option>
            {CAPABILITIES.map((s) => (
              <option key={s} value={s}>{roleLabel(s)}</option>
            ))}
          </select>
        </Field>
      </FilterRow>
      <ActiveFilters values={{ query, status, capability: cap }} labels={{ query: "Search", status: "Employment status", capability: "Capability" }} clear={(key) => filter(key === "query" ? setQuery : key === "status" ? setStatus : setCap, "")} />
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {q.data && (
        <>
          {!q.data.items.length ? (
            <Empty heading={query || status || cap ? "No matching people" : "No people yet"} action={query || status || cap ? <button className="btn" onClick={() => { setQuery(""); setStatus(""); setCap(""); setPages([null]); }}>Clear filters</button> : undefined}>{query || status || cap ? "No people match these filters." : "People appear here when an account is registered."}</Empty>
          ) : (
            <Table
              label="People roster"
              head={
                <tr>
                  <th>Person / account</th>
                  <th>Standing capabilities</th>
                  <th>Employment status</th>
                  <th>Last sign-in</th>
                </tr>
              }
            >
              {q.data.items.map((p) => (
                <tr key={p.person_id}>
                  <td>
                    <button className="link" onClick={() => setPerson(p.person_id)}>
                      {p.name}
                    </button>
                    <div className="muted">
                      {p.person_id} · {p.account ?? "No sign-in account"}
                    </div>
                  </td>
                  <td>
                    <div className="acts">
                      {p.capabilities.map((c) => (
                        <Pill tone="muted" category key={c}>
                          {roleLabel(c)}
                        </Pill>
                      ))}
                    </div>
                  </td>
                  <td>
                    <Pill tone={p.employment_status === "inactive" ? "muted" : "ok"}>{p.employment_status}</Pill>
                  </td>
                  <td>{fmtDate(p.last_sign_in_at, true)}</td>
                </tr>
              ))}
            </Table>
          )}
          <Pager count={q.data.items.length} page={pages.length}
            previous={pages.length > 1 ? () => setPages(pages.slice(0, -1)) : undefined}
            next={q.data.next_cursor ? () => setPages([...pages, q.data!.next_cursor!]) : undefined}
          />
        </>
      )}
      {person && <PersonDetail id={person} onClose={() => setPerson(null)} />}
      <ReasonDialog
        title="View as a person"
        confirm="Start viewing"
        open={lens}
        onClose={() => setLens(false)}
        error={start.error}
        busy={start.isPending}
        onConfirm={(reason) =>
          start.mutate(
            { person_id: target, reason },
            {
              onSuccess: () => {
                window.location.hash = "#today";
                window.location.reload();
              },
            },
          )
        }
      >
        <p>Read-only for 30 minutes. Every read is receipted. Stop viewing before making a change.</p>
        <Banner error={everyone.error} />
        <Field label="Person" required id="lens-target">
          <select id="lens-target" required value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Choose a person</option>
            {everyone.data?.map((p) => (
              <option key={p.person_id} value={p.person_id}>
                {p.name} · {p.capabilities.map(roleLabel).join(", ")} · {p.employment_status}
              </option>
            ))}
          </select>
        </Field>
      </ReasonDialog>
      {showsGlobalAccess(can) && <GlobalAccess />}
    </section>
  );
}
function PersonDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const directory = useRead(["all-people"], allPeople);
  const names = new Map((directory.data ?? []).map((person) => [person.person_id, person.name]));
  const q = useRead(["person", id], () => adminPersonOpen({ person_id: id }));
  const [editing, setEditing] = useState(false);
  const [caps, setCaps] = useState<string[]>([]);
  const save = useWrite(adminSetCapabilities, [["people"], ["all-people"], ["person", id]]);
  return (
    <Dialog title={q.data?.name ?? "Person"} open onClose={onClose}>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {q.data && (
        <>
          <KV
            items={[
              ["Account", q.data.account],
              ["Employment status", q.data.employment_status],
              ["Department", q.data.department],
              ["Title", q.data.title],
              ["Last sign-in", fmtDate(q.data.last_sign_in_at, true)],
            ]}
          />
          <button
            className="btn"
            onClick={() => {
              setCaps(q.data!.capabilities);
              setEditing(true);
            }}
          >
            Set capabilities
          </button>
          {save.isSuccess && <Receipt>Capabilities updated. Earlier assignments remain in the history.</Receipt>}
          <section><h3 className="sec">Capability history</h3><Banner error={directory.error} /><Table label="Capability history" head={<tr><th scope="col">Role</th><th scope="col">Granted</th><th scope="col">Granted by</th><th scope="col">Revoked</th></tr>}>{q.data.capability_history.map((entry, i) => <tr key={i}><th scope="row">{roleLabel(String(entry.role))}</th><td>{fmtDate(entry.granted_at as string, true)}</td><td>{personLabel(names.get(String(entry.granted_by_id)), entry.granted_by_id as string)}</td><td>{entry.revoked_at ? <>{fmtDate(String(entry.revoked_at), true)} · {personLabel(names.get(String(entry.revoked_by_id)), entry.revoked_by_id as string)}</> : "Not revoked"}</td></tr>)}</Table><details><summary>Technical details</summary><RecordFields value={q.data.capability_history.map((entry, i) => ({ record: i + 1, id: entry.id }))} /></details></section>
          <section><h3 className="sec">Seats held</h3><Table label="Seats held" head={<tr><th scope="col">Entity</th><th scope="col">Id</th><th scope="col">Seat</th><th scope="col">Since</th><th scope="col">Live</th></tr>}>{q.data.seats.map((seat, i) => <tr key={i}><td>{String(seat.entity_type)}</td><td className="mono">{String(seat.entity_id)}</td><td>{roleLabel(String(seat.seat))}</td><td>{fmtDate(seat.since as string, true)}</td><td><Pill tone={seat.live ? "info" : "muted"}>{seat.live ? "Live" : "Ended"}</Pill></td></tr>)}</Table></section>
          {[
            ["Access grants", q.data.grants],
            ["Stand-ins", q.data.stand_ins],
            ["Recent receipts", q.data.receipts],
          ].map(([label, value]) => (
            <section key={String(label)}>
              <h3 className="sec">{String(label)}</h3>
              <RecordFields value={value} />
            </section>
          ))}
        </>
      )}
      <ReasonDialog
        title="Set capabilities"
        confirm="Save capabilities"
        open={editing}
        onClose={() => setEditing(false)}
        busy={save.isPending}
        error={save.error}
        onConfirm={(reason) => save.mutate({ person_id: id, capabilities: caps, reason }, { onSuccess: () => setEditing(false) })}
      >
        <p>Revocations preserve earlier assignments. A live seat or the last active Admin can prevent a revocation.</p>
        <fieldset className="radios">
          <legend>Standing capabilities</legend>
          {ROLES.map((r) => (
            <label key={r} className="opt">
              <input type="checkbox" checked={caps.includes(r)} onChange={(e) => setCaps(e.target.checked ? [...caps, r] : caps.filter((c) => c !== r))} />
              {roleLabel(r)}
            </label>
          ))}
        </fieldset>
      </ReasonDialog>
    </Dialog>
  );
}
function GlobalAccess() {
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const q = useRead(["global-access", pages.at(-1)], () => accessListGlobal({ limit: 25, cursor: pages.at(-1) ?? null }));
  const [action, setAction] = useState<{ rid: string; id: string; kind: "grant" | "decline" } | null>(null);
  const [scope, setScope] = useState<"read" | "cover">("read");
  const [days, setDays] = useState(7);
  const approve = useWrite(accessApproveRequest, [["global-access"]]);
  const decline = useWrite(accessDecline, [["global-access"]]);
  return (
    <section>
      <h2 className="sec">Global access requests and grants</h2>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {q.data && (
        <>
          {q.data.items.every((r) => !r.requests.length && !r.grants.length) && <Empty heading="No access arrangements">No access requests or grants.</Empty>}
          {q.data.items
            .filter((r) => r.requests.length || r.grants.length)
            .map((r) => (
              <section key={r.requisition_id}>
                <h3 className="sec">
                  <a href={`#requisitions/${r.requisition_id}/access`}>{r.requisition_id} · Access &amp; stand-ins</a>
                </h3>
                {r.requests.length > 0 && (
                  <Table
                    label={`Requests ${r.requisition_id}`}
                    head={
                      <tr>
                        <th>Requester / reason</th>
                        <th>Requested scope</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    }
                  >
                    {r.requests.map((a) => (
                      <tr key={a.id}>
                        <td>
                          {a.requester_name}
                          <div className="long">{a.reason}</div>
                        </td>
                        <td>{a.scope_label}</td>
                        <td>{a.status}</td>
                        <td>
                          {a.status === "requested" && (
                            <div className="acts">
                              <button className="btn btn--sm" onClick={() => setAction({ rid: r.requisition_id, id: a.id, kind: "grant" })}>
                                Grant
                              </button>
                              <button className="btn btn--sm" onClick={() => setAction({ rid: r.requisition_id, id: a.id, kind: "decline" })}>
                                Decline
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </Table>
                )}
                {r.grants.length > 0 && (
                  <Table
                    label={`Grants ${r.requisition_id}`}
                    head={
                      <tr>
                        <th>Grantee / grantor</th>
                        <th>Scope</th>
                        <th>Expires</th>
                        <th>State</th>
                      </tr>
                    }
                  >
                    {r.grants.map((g) => (
                      <tr key={g.id}>
                        <td>
                          {g.grantee_name}
                          <div className="muted">by {g.granted_by_name}</div>
                        </td>
                        <td>{g.scope_label}</td>
                        <td>{fmtDate(g.expires_at, true)}</td>
                        <td>
                          <Pill tone={g.live ? "ok" : "muted"}>{g.revoked_at ? "revoked" : g.live ? "live" : "expired"}</Pill>
                        </td>
                      </tr>
                    ))}
                  </Table>
                )}
              </section>
            ))}
          <Pager count={q.data.items.length} page={pages.length}
            previous={pages.length > 1 ? () => setPages(pages.slice(0, -1)) : undefined}
            next={q.data.next_cursor ? () => setPages([...pages, q.data!.next_cursor!]) : undefined} />
        </>
      )}
      <ReasonDialog
        title={action?.kind === "grant" ? "Grant access" : "Decline access"}
        confirm={action?.kind === "grant" ? "Grant" : "Decline"}
        open={!!action}
        onClose={() => setAction(null)}
        error={approve.error ?? decline.error}
        busy={approve.isPending || decline.isPending}
        onConfirm={(reason) => {
          if (!action) return;
          if (action.kind === "grant")
            approve.mutate({ requisition_id: action.rid, access_request_id: action.id, scope, days, reason }, { onSuccess: () => setAction(null) });
          else decline.mutate({ requisition_id: action.rid, access_request_id: action.id, reason }, { onSuccess: () => setAction(null) });
        }}
      >
        {action?.kind === "grant" && (
          <>
            <Field label="Scope" id="global-scope">
              <select id="global-scope" value={scope} onChange={(e) => setScope(e.target.value as "read" | "cover")}>
                <option value="read">Read — no writes</option>
                <option value="cover">Cover — owner-equivalent, with the access exceptions</option>
              </select>
            </Field>
            <Field label="Days (1–30)" id="global-days">
              <input id="global-days" type="number" min={1} max={30} value={days} onChange={(e) => setDays(e.target.valueAsNumber)} />
            </Field>
          </>
        )}
      </ReasonDialog>
    </section>
  );
}
export function AdminDelegation() {
  const [filters, setFilters] = useState({ seat: "", grantor: "", grantee: "", requisition: "", state: "" });
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const q = useRead(["global-delegation", filters, pages.at(-1)], () => delegationListGlobal({ ...filters, limit: 25, cursor: pages.at(-1) ?? null }));
  const [id, setId] = useState<string | null>(null);
  const revoke = useWrite(delegationRevoke, [["global-delegation"]]);
  const rows = q.data?.items;
  return (
    <section>
      <h2 className="sec">Global stand-ins</h2>
      <p className="muted">The seat holder's own action wins. A revoked or expired grant remains in the history.</p>
      <div className="admin-filters">
        {Object.entries(filters).map(([k, v]) => (
          <Field key={k} label={k} id={`deleg-${k}`}>
            <input id={`deleg-${k}`} value={v} onChange={(e) => { setFilters({ ...filters, [k]: e.target.value }); setPages([null]); }} />
          </Field>
        ))}
      </div>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {rows &&
        (!rows.length ? (
          <Empty heading={Object.values(filters).some(Boolean) ? "No matching stand-ins" : "No stand-ins yet"} action={Object.values(filters).some(Boolean) ? <button className="btn" onClick={() => { setFilters({ seat: "", grantor: "", grantee: "", requisition: "", state: "" }); setPages([null]); }}>Clear filters</button> : undefined}>{Object.values(filters).some(Boolean) ? "No stand-ins match these filters." : "Named stand-ins appear here when a seat holder appoints one."}</Empty>
        ) : (
          <Table
            label="Global stand-ins"
            head={
              <tr>
                <th>Stand-in / for</th>
                <th>Seat / requisition</th>
                <th>May</th>
                <th>Window / state</th>
                <th>Action</th>
              </tr>
            }
          >
            {rows.map((d) => (
              <tr key={d.id}>
                <td>
                  {d.grantee_name}
                  <div className="muted">for {d.grantor_name}</div>
                </td>
                <td>
                  {d.seat_label}
                  <br />
                  <a href={`#requisitions/${d.requisition_id}/access`}>{d.requisition_id}</a>
                </td>
                <td>{d.capability_labels.join(" · ")}</td>
                <td>
                  {fmtDate(d.valid_from)} → {fmtDate(d.valid_to)}
                  <br />
                  <Pill tone={d.live ? "ok" : "muted"}>{d.status}</Pill>
                </td>
                <td>
                  {["active", "proposed"].includes(d.status) && (
                    <button className="btn btn--sm" onClick={() => setId(d.id)}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        ))}
      {q.data && <Pager count={q.data.items.length} page={pages.length}
        previous={pages.length > 1 ? () => setPages(pages.slice(0, -1)) : undefined}
        next={q.data.next_cursor ? () => setPages([...pages, q.data!.next_cursor!]) : undefined} />}
      {revoke.isSuccess && <Receipt>Stand-in revoked; earlier acts remain attributed.</Receipt>}
      <ReasonDialog
        title="Revoke stand-in"
        confirm="Revoke"
        open={!!id}
        onClose={() => setId(null)}
        busy={revoke.isPending}
        error={revoke.error}
        onConfirm={(reason) => revoke.mutate({ delegation_id: id!, reason }, { onSuccess: () => setId(null) })}
      />
    </section>
  );
}
